/**
 * When this car must stop, and why.
 *
 * The one question a pit wall asks continuously, and the reason this is one
 * engine rather than a pit-window feature. Every rule that forces a stop
 * reduces to a deadline:
 *
 *   a window closing        a driver-change window is about to shut
 *   a stint cap             the longest a driver may be out
 *   a driver's budget       their cumulative limit for the race
 *   fuel                    later; the same shape
 *
 * The earliest one wins and the widget names it. A race with windows and a race
 * with none ask the same question and get the same answer in the same words.
 *
 * The decision is never "how long until the deadline". A car can only pit as it
 * passes the pit entry, once a lap, so the real question is how many more times
 * it will pass before the deadline — counted from the last time it crossed the
 * line, not from now, because a lap already half run is half an opportunity
 * closer.
 */

export type ConstraintKind = 'window' | 'stint' | 'driverBudget' | 'fuel'

export interface Constraint {
  kind: ConstraintKind
  deadline: Date
  /** Already phrased for display: "window closes", "stint limit". */
  label: string
}

/**
 * How safe it is to skip this time round.
 *
 *   comfortable  another lap is safe even if it is neutralised
 *   tight        another lap is safe only if nothing happens — the FCY risk
 *   critical     another lap breaches even at racing pace: pit this time
 *   missed       the deadline cannot be reached at all
 *   unknown      not enough information, or the race is stopped
 */
export type StopRisk = 'comfortable' | 'tight' | 'critical' | 'missed' | 'unknown'

export interface StopByInput {
  now: Date
  /** When this car last crossed the line. The clock the decision runs on. */
  lastCrossing: Date | null
  /** Mean green lap this stint. */
  typicalLapMs: number | null
  /** What a lap costs under a full-course yellow. */
  fcyLapMs: number | null
  /** True while the race is stopped — laps are not being completed. */
  raceStopped?: boolean
}

export interface StopBy {
  constraint: Constraint | null
  /** Opportunities to pit before the deadline, at racing pace. */
  laps: number | null
  risk: StopRisk
  /** One sentence, already phrased. */
  detail: string
}

/** The earliest deadline still ahead, which is the only one that matters. */
export function bindingConstraint(
  constraints: readonly Constraint[],
  now: Date,
): Constraint | null {
  const ahead = constraints.filter((c) => c.deadline.getTime() > now.getTime())
  const pool = ahead.length > 0 ? ahead : constraints
  if (pool.length === 0) return null
  return [...pool].sort((a, b) => a.deadline.getTime() - b.deadline.getTime())[0] ?? null
}

/**
 * Work out whether this car can afford another lap.
 *
 * Opportunities land at `lastCrossing + n × lap`. The next one is `t1`; the one
 * after it is `t2`, and the question is whether `t2` is still inside the
 * deadline — because skipping `t1` is the decision actually being made.
 *
 * `t2` is tested twice: once at racing pace, and once assuming the lap after
 * `t1` is neutralised. The gap between those two answers is the risk, and it is
 * exactly the case a driver reasons about out loud — "if they throw a yellow as
 * I come past the pit entry, do I still get back round in time?"
 */
export function stopBy(constraint: Constraint | null, input: StopByInput): StopBy {
  if (!constraint) {
    return { constraint: null, laps: null, risk: 'unknown', detail: 'Nothing forces a stop.' }
  }

  // `now` is deliberately not read: the decision runs from the last line
  // crossing, because a lap already half run is half an opportunity closer.
  const { lastCrossing, typicalLapMs, fcyLapMs, raceStopped } = input
  const deadline = constraint.deadline.getTime()

  if (raceStopped) {
    // Stationary under a red flag the car completes no laps, so a lap count
    // would be a lie about the one thing it is meant to answer.
    return {
      constraint,
      laps: null,
      risk: 'unknown',
      detail: `Race stopped. ${constraint.label} at ${clock(constraint.deadline)}.`,
    }
  }

  if (!lastCrossing || !typicalLapMs || typicalLapMs <= 0) {
    return {
      constraint,
      laps: null,
      risk: 'unknown',
      detail: `${cap(constraint.label)} at ${clock(constraint.deadline)}. No lap time yet.`,
    }
  }

  const t1 = lastCrossing.getTime() + typicalLapMs
  const t2 = t1 + typicalLapMs
  const t2Worst = t1 + (fcyLapMs && fcyLapMs > 0 ? fcyLapMs : typicalLapMs)

  const laps = opportunitiesBefore(lastCrossing.getTime(), typicalLapMs, deadline)

  if (t1 > deadline) {
    return {
      constraint,
      laps: 0,
      risk: 'missed',
      detail: `${cap(constraint.label)} at ${clock(constraint.deadline)} — before this lap ends.`,
    }
  }
  if (t2 > deadline) {
    return {
      constraint,
      laps,
      risk: 'critical',
      detail: `Pit this time round. ${cap(constraint.label)} at ${clock(constraint.deadline)}.`,
    }
  }
  if (t2Worst > deadline) {
    return {
      constraint,
      laps,
      risk: 'tight',
      detail: `One more lap is safe unless it is neutralised. ${cap(constraint.label)} at ${clock(constraint.deadline)}.`,
    }
  }
  return {
    constraint,
    laps,
    risk: 'comfortable',
    detail: `${cap(constraint.label)} at ${clock(constraint.deadline)}.`,
  }
}

/** How many times the car passes the pit entry before the deadline. */
function opportunitiesBefore(from: number, lapMs: number, deadline: number): number {
  if (lapMs <= 0 || deadline <= from) return 0
  return Math.floor((deadline - from) / lapMs)
}

// ------------------------------------------------------------- lap times

/**
 * What a lap costs under a full-course yellow.
 *
 * Measured from the session's own neutralised laps wherever there have been
 * any — every lap carries per-sector FCY flags, so this is the field's real
 * answer rather than a rule of thumb. Before the first yellow it falls back to
 * the circuit's length at neutralisation speed, and to a multiple of the green
 * lap when even that is unknown.
 */
export function fcyLapEstimate(options: {
  measuredMs?: number | null
  trackLengthMetres?: number | null
  typicalLapMs?: number | null
}): number | null {
  const { measuredMs, trackLengthMetres, typicalLapMs } = options
  if (measuredMs && measuredMs > 0) return measuredMs

  if (trackLengthMetres && trackLengthMetres > 0) {
    // Sixty km/h is the usual neutralisation speed, and it lands within a few
    // seconds of the seven minutes a seven-kilometre circuit actually takes.
    return Math.round((trackLengthMetres / (60_000 / 3600)) * 1000)
  }
  if (typicalLapMs && typicalLapMs > 0) return Math.round(typicalLapMs * fcyMultiple)
  return null
}

/** How much slower a neutralised lap is, when nothing better is known. */
const fcyMultiple = 2.2

/** The mean of whatever laps were run under a yellow, across the whole field. */
export function measuredFcyLapMs(
  laps: readonly { lapTimeMs?: number | null; s1Fcy?: boolean; s2Fcy?: boolean; s3Fcy?: boolean }[],
): number | null {
  let sum = 0
  let n = 0
  for (const l of laps) {
    if (!l.lapTimeMs || l.lapTimeMs <= 0) continue
    // Every sector neutralised, not merely one: a lap that went yellow halfway
    // is neither a green lap nor a yellow one, and averaging it in would drag
    // the estimate towards a time nobody will actually do.
    if (!l.s1Fcy || !l.s2Fcy || !l.s3Fcy) continue
    sum += l.lapTimeMs
    n++
  }
  return n > 0 ? Math.round(sum / n) : null
}

// ------------------------------------------------------------------ words

function clock(d: Date): string {
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
}

function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}
